import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Stethoscope, AlertTriangle, ShieldCheck, UserRound, FileText, KeyRound, Copy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  } catch (_) {
    return String(v);
  }
};

const AdminDiagnostics = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runLookup = async (e) => {
    e?.preventDefault?.();
    const q = email.trim();
    if (!q) {
      toast.error('Enter a client email to look up');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/admin/diagnostics/client-lookup`, {
        params: { email: q },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
      if ((res.data.client_records || []).length === 0) {
        toast.info('No client record found for that email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    toast.success('Diagnostic JSON copied');
  };

  const records = result?.client_records || [];
  const duplicates = result?.duplicate_groups || [];

  return (
    <div className="space-y-6" data-testid="admin-diagnostics-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center">
            <Stethoscope className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Client &amp; Will Diagnostics</h1>
            <p className="text-sm text-slate-400">
              Trace which client_id owns which wills — pinpoint stale-session vs duplicate-record issues.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <form
        onSubmit={runLookup}
        className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row gap-3"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Client email (e.g. maskyrohit@gmail.com)"
            className="pl-9 bg-slate-950 border-slate-700 text-white"
            data-testid="diagnostics-email-input"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
          data-testid="diagnostics-search-btn"
        >
          {loading ? 'Looking up…' : 'Run Lookup'}
        </Button>
      </form>

      {result && (
        <div className="space-y-6" data-testid="diagnostics-results">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard icon={UserRound} label="Client records" value={records.length} />
            <SummaryCard icon={FileText} label="Total wills" value={result.total_wills} />
            <SummaryCard
              icon={KeyRound}
              label="Active sessions"
              value={result.active_session_count}
              tone={result.active_session_count > 0 ? 'warn' : 'ok'}
            />
            <SummaryCard
              icon={AlertTriangle}
              label="Same-partner dups"
              value={duplicates.length}
              tone={duplicates.length > 0 ? 'danger' : 'ok'}
            />
          </div>

          {/* Analysis */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-amber-400" /> Analysis
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={copyJson}
                className="border-slate-700 text-slate-300"
                data-testid="diagnostics-copy-json"
              >
                <Copy className="w-4 h-4 mr-1" /> Copy JSON
              </Button>
            </div>
            <ul className="space-y-2" data-testid="diagnostics-analysis">
              {(result.analysis || []).map((line, i) => (
                <li key={i} className="text-sm text-slate-300 flex gap-2">
                  <span className="text-amber-400">›</span>
                  <span>{line}</span>
                </li>
              ))}
              {(result.analysis || []).length === 0 && (
                <li className="text-sm text-slate-500">No notable findings.</li>
              )}
            </ul>
          </div>

          {/* Client records + their wills */}
          {records.map((c) => {
            const wills = (result.wills_by_client_id || {})[c.client_id] || [];
            return (
              <div
                key={c.client_id}
                className="bg-slate-900 border border-slate-800 rounded-xl p-4"
                data-testid={`diagnostics-client-${c.client_id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-white font-semibold">{c.name || '—'} <span className="text-slate-500 font-normal">({c.email})</span></p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      client_id: <span className="font-mono text-slate-300">{c.client_id}</span>
                    </p>
                    <p className="text-xs text-slate-400">
                      Partner: {c.partner_name || '—'} · subdomain: <span className="text-slate-300">{c.partner_subdomain || '—'}</span>
                    </p>
                  </div>
                  <div className="text-right text-xs text-slate-400 space-y-0.5">
                    <p>source: <span className="text-slate-300">{c.source || '—'}</span></p>
                    <p>has password: <span className="text-slate-300">{String(c.has_password)}</span> · verified: <span className="text-slate-300">{String(c.email_verified)}</span></p>
                    <p>first login: {fmt(c.first_login)}</p>
                    <p>last login: {fmt(c.last_login)}</p>
                  </div>
                </div>

                {wills.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-slate-800">
                          <th className="py-2 pr-3 font-medium">Testator</th>
                          <th className="py-2 pr-3 font-medium">Status</th>
                          <th className="py-2 pr-3 font-medium">Created by</th>
                          <th className="py-2 pr-3 font-medium">Paid</th>
                          <th className="py-2 pr-3 font-medium">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wills.map((w) => (
                          <tr key={w.will_id} className="border-b border-slate-800/60">
                            <td className="py-2 pr-3 text-white">{w.testator_name || '—'}</td>
                            <td className="py-2 pr-3 text-slate-300">{w.status || '—'}</td>
                            <td className="py-2 pr-3 text-slate-300">{w.created_by || 'client'}</td>
                            <td className="py-2 pr-3 text-slate-300">{String(w.is_paid)}</td>
                            <td className="py-2 pr-3 text-slate-400">{fmt(w.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No wills under this client_id.</p>
                )}
              </div>
            );
          })}

          {/* Sessions */}
          {(result.sessions || []).length > 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-amber-400" /> Auth sessions
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-800">
                      <th className="py-2 pr-3 font-medium">client_id</th>
                      <th className="py-2 pr-3 font-medium">Created</th>
                      <th className="py-2 pr-3 font-medium">Last used</th>
                      <th className="py-2 pr-3 font-medium">State</th>
                      <th className="py-2 pr-3 font-medium">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.sessions.map((s) => (
                      <tr key={s.session_id} className="border-b border-slate-800/60">
                        <td className="py-2 pr-3 font-mono text-xs text-slate-300">{s.client_id}</td>
                        <td className="py-2 pr-3 text-slate-400">{fmt(s.created_at)}</td>
                        <td className="py-2 pr-3 text-slate-400">{fmt(s.last_used_at)}</td>
                        <td className="py-2 pr-3">
                          {s.revoked_at ? (
                            <span className="text-slate-500">revoked</span>
                          ) : (
                            <span className="text-emerald-400">active</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-slate-400">{s.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, tone = 'neutral' }) => {
  const toneClass = {
    neutral: 'text-slate-300',
    ok: 'text-emerald-400',
    warn: 'text-amber-400',
    danger: 'text-red-400',
  }[tone];
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <p className={`text-2xl font-bold ${toneClass}`}>{value ?? 0}</p>
    </div>
  );
};

export default AdminDiagnostics;
